/**
 * SkyBackground — fixed back layer behind the Home/Learn page.
 *
 * iOS source: SkyBackground.tsx + SpinningSun.tsx.
 *   - Vertical gradient darkBlue (#003d8e) → blue (#0c92f1) → white at [0, 0.6, 1].
 *   - A spinning smiley sun top-right (size 140, top 15% of screen, right -10,
 *     opacity 0.45). Only the ray halo rotates; the face stays static.
 *   - Realistic white clouds (cloud-1.png / cloud-2.png) scattered at low opacity.
 *   - No grass: on iOS the grass band exists only on the Profile screen.
 *
 * Desktop: the gradient runs full-bleed edge to edge while the sun + clouds
 * hug the centered max-600px content column (same pattern as AppBackground's
 * SkyDecor), so wide viewports read as the phone scene with the sky extended.
 *
 * Assets cloud-1.png, cloud-2.png, sunfull2.png and sunface.png are copied
 * verbatim from client-native/assets. The whole layer is pointer-events:none
 * so it never blocks taps on the content above.
 */
import { FC } from "react";
import cloud1 from "../../assets/profile/cloud-1.png";
import cloud2 from "../../assets/profile/cloud-2.png";
import SpinningSun from "../decorations/SpinningSun";
import { CLOUD_EDGE_FADE_MASK } from "../decorations/cloudEdgeFade";

const COLUMN_WIDTH = 600; // matches PageContainer / the Home content column.

// Cloud layout mirrors iOS SkyBackground.tsx:38-46. iOS uses absolute px
// against the ~390×844 reference device; tops translate to viewport-height %
// and widths to % of the content column (≈ the phone width on mobile, capped
// at 600px on desktop) so the scatter scales with the page. Raster cloud PNGs,
// rendered inside the edge-fade layer so they dissolve at the column edges.
const CLOUDS: {
  src: string;
  top: string;
  left?: string;
  right?: string;
  w: number; // % of the content column
  opacity: number;
}[] = [
  { src: cloud1, top: "7%", left: "-8%", w: 48, opacity: 0.35 },
  { src: cloud2, top: "1%", right: "-7%", w: 45, opacity: 0.25 },
  { src: cloud2, top: "21%", right: "5%", w: 53, opacity: 0.25 },
  { src: cloud2, top: "33%", left: "3%", w: 45, opacity: 0.35 },
  { src: cloud1, top: "43%", right: "-2%", w: 48, opacity: 0.3 },
  { src: cloud2, top: "57%", left: "11%", w: 58, opacity: 0.4 },
  { src: cloud2, top: "70%", right: "-5%", w: 58, opacity: 0.35 },
  { src: cloud1, top: "70%", left: "-5%", w: 46, opacity: 0.35 },
  { src: cloud2, top: "84%", left: "16%", w: 80, opacity: 0.4 },
];

const SkyBackground: FC = () => {
  return (
    <div
      aria-hidden
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        overflow: "hidden",
        // iOS gradient: darkBlue → blue at 0.6 → white at 1 (SkyBackground.tsx:17,26).
        background:
          "linear-gradient(180deg, #003d8e 0%, #0c92f1 60%, #FFFFFF 100%)",
      }}
    >
      {/* Sun + clouds anchored to the centered phone-width column. Clipped to
          the column so negative-offset clouds (and the bleeding sun) cut off at
          the column edge exactly as they cut off at the phone screen edge on
          iOS — on desktop they must not spill into the empty side gutters. */}
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          maxWidth: COLUMN_WIDTH,
          overflow: "hidden",
        }}
      >
        {/* Two-layer sun mascot, top ~15%, right edge, opacity 0.45. Only the
            rays rotate; the smiling face stays static (matches iOS SpinningSun). */}
        <SpinningSun
          size={140}
          spinSeconds={18}
          style={{ top: "13%", right: -10, opacity: 0.45 }}
        />

        {/* Clouds in their own layer, soft-faded at the column edges so they
            dissolve into the sky instead of hard-clipping / spilling into the
            desktop side gutters. */}
        <div style={{ position: "absolute", inset: 0, ...CLOUD_EDGE_FADE_MASK }}>
          {CLOUDS.map((c, i) => (
            <img
              key={i}
              src={c.src}
              alt=""
              aria-hidden
              style={{
                position: "absolute",
                top: c.top,
                left: c.left,
                right: c.right,
                width: `${c.w}%`,
                height: "auto",
                opacity: c.opacity,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SkyBackground;
